import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Typography, 
    Chip,
    Skeleton,
    Button,
    TextField,
    Select,
    MenuItem,
    Switch,
    IconButton,
    CircularProgress,
    Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { RestaurantService } from '../../../api/services/restaurantService';
import { toast } from '../../../utils/toast';

const DAYS_MAP = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
];

export default function OperatingHoursTab() {
    const [hours, setHours] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Edit mode states
    const [editMode, setEditMode] = useState(false);
    const [editHours, setEditHours] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchOperatingHours();
    }, []);

    const fetchOperatingHours = async () => {
        setLoading(true);
        try {
            const data = await RestaurantService.getOperatingHours();
            const schedules = Array.isArray(data) ? data : data?.hours || data?.schedules || [];
            
            // Sort schedules by day of week starting from Sunday (0 to 6)
            const sorted = [...schedules].sort((a, b) => {
                const dayA = (a.dayOfWeek ?? a.day) === 7 ? 0 : (a.dayOfWeek ?? a.day ?? 0);
                const dayB = (b.dayOfWeek ?? b.day) === 7 ? 0 : (b.dayOfWeek ?? b.day ?? 0);
                return dayA - dayB;
            });

            // Map the API data
            setHours(sorted);
        } catch (error) {
            console.error('Failed to fetch operating hours:', error);
            // Global interceptor handles the toast
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = () => {
        // Prepare the array for editing. Map any existing objects into the strict schema.
        const editable = hours.length > 0 ? hours.map(h => {
            const rawDay = h.dayOfWeek ?? h.day;
            const parsedDay = rawDay !== undefined && rawDay !== null ? (parseInt(rawDay, 10) === 7 ? 0 : parseInt(rawDay, 10)) : 0;
            return {
                dayOfWeek: parsedDay,
                openTime: h.openTime || h.openingTime || "07:00",
                closeTime: h.closeTime || h.closingTime || "22:00",
                isClosed: !!h.isClosed,
                specialDate: h.specialDate || ""
            };
        }) : [
            { dayOfWeek: 0, openTime: "07:00", closeTime: "22:00", isClosed: false, specialDate: "" }
        ];
        
        setEditHours(editable);
        setEditMode(true);
    };

    const handleAddRow = () => {
        setEditHours([
            ...editHours,
            { dayOfWeek: 0, openTime: "07:00", closeTime: "22:00", isClosed: false, specialDate: "" }
        ]);
    };

    const handleRemoveRow = (index) => {
        setEditHours(editHours.filter((_, i) => i !== index));
    };

    const handleChange = (index, field, value) => {
        const updated = [...editHours];
        updated[index][field] = value;
        setEditHours(updated);
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            // Strictly clean payload array to match requested schema: dayOfWeek, openTime, closeTime, isClosed, specialDate
            const payloadHours = editHours.map(row => {
                const out = {
                    isClosed: !!row.isClosed
                };
                if (row.dayOfWeek !== undefined && row.dayOfWeek !== null && row.dayOfWeek !== '') {
                    const parsed = parseInt(row.dayOfWeek, 10);
                    out.dayOfWeek = parsed === 7 ? 0 : parsed;
                }
                
                if (!out.isClosed) {
                    out.openTime = String(row.openTime || "00:00");
                    out.closeTime = String(row.closeTime || "00:00");
                }
                if (row.specialDate && String(row.specialDate).trim() !== "") {
                    out.specialDate = String(row.specialDate).trim();
                }
                return out;
            });

            await RestaurantService.updateOperatingHours({ hours: payloadHours });
            
            toast.success('Operational hours and holiday overrides successfully updated');
            setEditMode(false);
            fetchOperatingHours();
        } catch (error) {
            console.error('Failed to update operating hours:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '--:--';
        try {
            const [hours, minutes] = timeStr.split(':');
            const date = new Date();
            date.setHours(parseInt(hours, 10));
            date.setMinutes(parseInt(minutes, 10));
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            return timeStr;
        }
    };

    const getDayLabel = (dayVal) => {
        if (dayVal === undefined || dayVal === null) return 'N/A';
        const numVal = parseInt(dayVal, 10) === 7 ? 0 : parseInt(dayVal, 10);
        const d = DAYS_MAP.find(x => x.value === numVal);
        return d ? d.label : dayVal;
    };

    const getDayShort = (dayVal) => {
        const shorts = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        if (dayVal === undefined || dayVal === null) return 'DAY';
        const num = parseInt(dayVal, 10) === 7 ? 0 : parseInt(dayVal, 10);
        return shorts[num] || 'DAY';
    };

    return (
        <Box>
            {/* Header section with refined typography and badges */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                gap: 2,
                mb: 3 
            }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            bgcolor: 'rgba(37, 99, 235, 0.08)',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <ScheduleIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" fontWeight={700} color="#1E293B">
                                    Restaurant Operating Hours
                                </Typography>
                                <Chip 
                                    label={editMode ? "Editing Mode" : "Weekly Schedule"} 
                                    size="small"
                                    color={editMode ? "primary" : "default"}
                                    variant={editMode ? "filled" : "outlined"}
                                    sx={{ 
                                        fontWeight: 600, 
                                        fontSize: '0.72rem', 
                                        height: 22,
                                        borderRadius: '6px'
                                    }} 
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                                Configure daily service hours, active shifts, and holiday exceptions.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                
                {!editMode ? (
                    <Button 
                        variant="contained" 
                        startIcon={<EditIcon />}
                        onClick={handleEditClick}
                        disabled={loading}
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2.8,
                            py: 1,
                            boxShadow: '0 2px 10px rgba(37, 99, 235, 0.2)',
                            '&:hover': {
                                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)'
                            }
                        }}
                    >
                        Edit Schedules
                    </Button>
                ) : (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Button 
                            variant="outlined" 
                            startIcon={<CloseIcon />}
                            onClick={() => setEditMode(false)}
                            disabled={isSubmitting}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.2,
                                py: 1,
                                borderColor: '#CBD5E1',
                                color: '#64748B',
                                '&:hover': {
                                    borderColor: '#94A3B8',
                                    bgcolor: '#F8FAFC'
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={isSubmitting}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2.8,
                                py: 1,
                                boxShadow: '0 2px 10px rgba(37, 99, 235, 0.2)',
                                '&:hover': {
                                    boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)'
                                }
                            }}
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Main Table Container */}
            <TableContainer component={Paper}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell>Day of the Week</TableCell>
                            <TableCell>Opening Time</TableCell>
                            <TableCell>Closing Time</TableCell>
                            <TableCell>Special Holiday Date</TableCell>
                            <TableCell>Operational Status</TableCell>
                            {editMode && (
                                <TableCell align="center" sx={{ width: 80 }}>
                                    Actions
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from(new Array(7)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton width="60%" height={28} /></TableCell>
                                    <TableCell><Skeleton width="40%" height={28} /></TableCell>
                                    <TableCell><Skeleton width="40%" height={28} /></TableCell>
                                    <TableCell><Skeleton width="50%" height={28} /></TableCell>
                                    <TableCell><Skeleton width={90} height={28} /></TableCell>
                                </TableRow>
                            ))
                        ) : editMode ? (
                            editHours.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary" fontWeight={500}>
                                            No schedule blocks added. Click "Add Schedule Block" below.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                editHours.map((schedule, index) => {
                                    const isWeekend = schedule.dayOfWeek === 0 || schedule.dayOfWeek === 6;
                                    return (
                                        <TableRow 
                                            key={`edit-${index}`} 
                                            sx={{ 
                                                transition: 'background-color 0.15s ease',
                                                bgcolor: schedule.isClosed ? 'rgba(248, 250, 252, 0.7)' : '#FFFFFF',
                                                '&:hover': { bgcolor: schedule.isClosed ? '#F1F5F9' : '#F8FAFC' }
                                            }}
                                        >
                                            {/* Day Selection */}
                                            <TableCell sx={{ py: 1.6 }}>
                                                <Select
                                                    size="small"
                                                    value={schedule.dayOfWeek !== undefined && schedule.dayOfWeek !== null ? schedule.dayOfWeek : 0}
                                                    onChange={(e) => handleChange(index, 'dayOfWeek', e.target.value)}
                                                    disabled={isSubmitting}
                                                    sx={{ 
                                                        minWidth: 150,
                                                        borderRadius: '8px',
                                                        bgcolor: '#FFFFFF',
                                                        fontWeight: 600,
                                                        fontSize: '0.875rem',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#CBD5E1'
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#94A3B8'
                                                        }
                                                    }}
                                                >
                                                    {DAYS_MAP.map(d => (
                                                        <MenuItem key={d.value} value={d.value} sx={{ py: 1, fontSize: '0.875rem' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Box sx={{ 
                                                                    width: 8, 
                                                                    height: 8, 
                                                                    borderRadius: '50%', 
                                                                    bgcolor: d.value === 0 || d.value === 6 ? '#F59E0B' : '#3B82F6' 
                                                                }} />
                                                                <Typography variant="body2" fontWeight={500}>{d.label}</Typography>
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </TableCell>

                                            {/* Opening Time */}
                                            <TableCell sx={{ py: 1.6 }}>
                                                <TextField
                                                    size="small"
                                                    type="time"
                                                    value={schedule.openTime || ""}
                                                    onChange={(e) => handleChange(index, 'openTime', e.target.value)}
                                                    disabled={isSubmitting || schedule.isClosed}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ 
                                                        width: 140,
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                            bgcolor: schedule.isClosed ? '#F1F5F9' : '#FFFFFF',
                                                            fontSize: '0.875rem',
                                                            '& fieldset': {
                                                                borderColor: '#CBD5E1'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#94A3B8'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Closing Time */}
                                            <TableCell sx={{ py: 1.6 }}>
                                                <TextField
                                                    size="small"
                                                    type="time"
                                                    value={schedule.closeTime || ""}
                                                    onChange={(e) => handleChange(index, 'closeTime', e.target.value)}
                                                    disabled={isSubmitting || schedule.isClosed}
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ 
                                                        width: 140,
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                            bgcolor: schedule.isClosed ? '#F1F5F9' : '#FFFFFF',
                                                            fontSize: '0.875rem',
                                                            '& fieldset': {
                                                                borderColor: '#CBD5E1'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#94A3B8'
                                                            }
                                                        }
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Special Holiday Date */}
                                            <TableCell sx={{ py: 1.6 }}>
                                                <TextField
                                                    size="small"
                                                    type="date"
                                                    value={schedule.specialDate || ""}
                                                    onChange={(e) => handleChange(index, 'specialDate', e.target.value)}
                                                    disabled={isSubmitting}
                                                    InputLabelProps={{ shrink: true }}
                                                    helperText={schedule.specialDate ? "Overrides standard schedule" : ""}
                                                    sx={{ 
                                                        width: 170,
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px',
                                                            bgcolor: '#FFFFFF',
                                                            fontSize: '0.875rem',
                                                            '& fieldset': {
                                                                borderColor: '#CBD5E1'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#94A3B8'
                                                            }
                                                        },
                                                        '& .MuiFormHelperText-root': {
                                                            fontSize: '0.7rem',
                                                            color: '#6366F1',
                                                            m: 0,
                                                            mt: 0.3
                                                        }
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Operational Status */}
                                            <TableCell sx={{ py: 1.6 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Switch
                                                        size="small"
                                                        color="success"
                                                        checked={!schedule.isClosed}
                                                        onChange={(e) => handleChange(index, 'isClosed', !e.target.checked)}
                                                        disabled={isSubmitting}
                                                    />
                                                    <Chip
                                                        label={schedule.isClosed ? 'Closed' : 'Open'}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: '0.75rem',
                                                            height: 24,
                                                            borderRadius: '6px',
                                                            bgcolor: schedule.isClosed ? '#FEF2F2' : '#ECFDF5',
                                                            color: schedule.isClosed ? '#DC2626' : '#059669',
                                                            border: schedule.isClosed ? '1px solid #FECACA' : '1px solid #A7F3D0',
                                                        }}
                                                    />
                                                </Box>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="center" sx={{ py: 1.6 }}>
                                                <Tooltip title={editHours.length === 1 ? "At least one row required" : "Remove this block"}>
                                                    <span>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleRemoveRow(index)}
                                                            disabled={isSubmitting || editHours.length === 1}
                                                            sx={{
                                                                color: '#94A3B8',
                                                                borderRadius: '8px',
                                                                p: 0.8,
                                                                '&:hover': {
                                                                    color: '#DC2626',
                                                                    bgcolor: '#FEF2F2'
                                                                },
                                                                '&.Mui-disabled': {
                                                                    color: '#E2E8F0'
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )
                        ) : hours.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                    <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                        <ScheduleIcon sx={{ fontSize: 40, color: '#94A3B8', opacity: 0.6 }} />
                                        <Typography variant="body1" fontWeight={600} color="text.secondary">
                                            No operating schedules configured
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Click "Edit Schedules" to configure hours for the restaurant.
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            hours.map((schedule, index) => {
                                const dayVal = schedule.dayOfWeek ?? schedule.day;
                                const isWeekend = dayVal === 0 || dayVal === 6 || dayVal === 7;
                                return (
                                    <TableRow 
                                        key={schedule.id || index} 
                                        sx={{ 
                                            transition: 'background-color 0.15s ease',
                                            bgcolor: schedule.isClosed ? 'rgba(248, 250, 252, 0.6)' : '#FFFFFF',
                                            '&:hover': { bgcolor: schedule.isClosed ? '#F1F5F9' : '#F8FAFC' }
                                        }}
                                    >
                                        {/* Day Name with Initial Pill */}
                                        <TableCell sx={{ py: 1.8 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{
                                                    width: 38,
                                                    height: 38,
                                                    borderRadius: '10px',
                                                    bgcolor: isWeekend ? '#FEF3C7' : '#EEF2FF',
                                                    color: isWeekend ? '#B45309' : '#4F46E5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 700,
                                                    fontSize: '0.72rem',
                                                    letterSpacing: '0.04em'
                                                }}>
                                                    {getDayShort(dayVal)}
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={700} color="#1E293B">
                                                        {getDayLabel(dayVal)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {isWeekend ? 'Weekend Service' : 'Regular Weekday'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        {/* Opening Time */}
                                        <TableCell sx={{ py: 1.8 }}>
                                            {schedule.isClosed ? (
                                                <Typography variant="body2" color="#94A3B8" sx={{ fontStyle: 'italic' }}>
                                                    -- : --
                                                </Typography>
                                            ) : (
                                                <Box sx={{ 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center', 
                                                    gap: 0.8, 
                                                    bgcolor: '#F8FAFC', 
                                                    px: 1.4, 
                                                    py: 0.6, 
                                                    borderRadius: '8px', 
                                                    border: '1px solid #E2E8F0' 
                                                }}>
                                                    <AccessTimeIcon sx={{ fontSize: 16, color: '#64748B' }} />
                                                    <Typography variant="body2" fontWeight={600} color="#334155">
                                                        {formatTime(schedule.openTime || schedule.openingTime)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </TableCell>

                                        {/* Closing Time */}
                                        <TableCell sx={{ py: 1.8 }}>
                                            {schedule.isClosed ? (
                                                <Typography variant="body2" color="#94A3B8" sx={{ fontStyle: 'italic' }}>
                                                    -- : --
                                                </Typography>
                                            ) : (
                                                <Box sx={{ 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center', 
                                                    gap: 0.8, 
                                                    bgcolor: '#F8FAFC', 
                                                    px: 1.4, 
                                                    py: 0.6, 
                                                    borderRadius: '8px', 
                                                    border: '1px solid #E2E8F0' 
                                                }}>
                                                    <AccessTimeIcon sx={{ fontSize: 16, color: '#64748B' }} />
                                                    <Typography variant="body2" fontWeight={600} color="#334155">
                                                        {formatTime(schedule.closeTime || schedule.closingTime)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </TableCell>

                                        {/* Special Holiday Date */}
                                        <TableCell sx={{ py: 1.8 }}>
                                            {schedule.specialDate ? (
                                                <Chip 
                                                    icon={<CalendarTodayIcon sx={{ fontSize: '13px !important' }} />}
                                                    label={schedule.specialDate} 
                                                    size="small" 
                                                    sx={{ 
                                                        bgcolor: '#EFF6FF', 
                                                        color: '#1D4ED8', 
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        border: '1px solid #BFDBFE',
                                                        borderRadius: '6px'
                                                    }} 
                                                />
                                            ) : (
                                                <Typography variant="body2" color="#94A3B8" sx={{ fontSize: '0.85rem' }}>
                                                    Standard Schedule
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* Operational Status */}
                                        <TableCell sx={{ py: 1.8 }}>
                                            <Chip 
                                                icon={schedule.isClosed ? <CancelIcon sx={{ fontSize: '14px !important' }} /> : <CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                                                label={schedule.isClosed ? 'Closed / Holiday' : 'Open for Service'} 
                                                size="small" 
                                                sx={{ 
                                                    fontWeight: 700,
                                                    fontSize: '0.75rem',
                                                    height: 26,
                                                    borderRadius: '6px',
                                                    bgcolor: schedule.isClosed ? '#FEF2F2' : '#ECFDF5',
                                                    color: schedule.isClosed ? '#DC2626' : '#059669',
                                                    border: schedule.isClosed ? '1px solid #FECACA' : '1px solid #A7F3D0',
                                                    '& .MuiChip-icon': {
                                                        color: schedule.isClosed ? '#DC2626 !important' : '#059669 !important'
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {/* Bottom bar for Edit Mode */}
            {editMode && (
                <Box sx={{ 
                    mt: 2.5, 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1.5
                }}>
                    <Button 
                        startIcon={<AddIcon />} 
                        variant="outlined" 
                        onClick={handleAddRow}
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2.5,
                            py: 0.9,
                            borderStyle: 'dashed',
                            borderWidth: '1.5px',
                            borderColor: '#CBD5E1',
                            color: 'primary.main',
                            '&:hover': {
                                borderStyle: 'dashed',
                                borderWidth: '1.5px',
                                borderColor: 'primary.main',
                                bgcolor: 'rgba(37, 99, 235, 0.04)'
                            }
                        }}
                    >
                        Add Schedule Block
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                        Tip: You can add specific holiday overrides to adjust hours on holidays.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
